/**
 * Diagnostic accuracy database for orthopaedic special tests.
 * Iter 72.
 *
 * Each entry carries:
 *   - sens / spec — Sensitivity, Specificity (0-1)
 *   - lr_pos / lr_neg — Likelihood Ratios (positive / negative)
 *   - ref — Reference shortname (matches pe-options.js)
 *   - refFull — Full citation
 *   - refUrl — PubMed / DOI URL
 *   - condition — What the test is screening for
 *   - notes — Brief clinical interpretation
 *
 * Interpretation:
 *   • Sens > 0.85 → useful to RULE OUT (negative test rules condition out — "SnNout")
 *   • Spec > 0.85 → useful to RULE IN (positive test confirms — "SpPin")
 *   • LR+ ≥ 10 → large, conclusive shift in probability when positive
 *   • LR+ 5-10 → moderate shift
 *   • LR− ≤ 0.1 → large shift toward ruling out when negative
 *
 * Values are point estimates from peer-reviewed meta-analyses where available.
 * For tests where literature varies, we picked the most cited / largest study.
 */
export const TEST_ACCURACY = {
  // ─────── Shoulder ───────
  'Hawkins-Kennedy': {
    condition: 'Subacromial impingement / rotator cuff pathology',
    sens: 0.74, spec: 0.57, lr_pos: 1.7, lr_neg: 0.46,
    ref: 'Hegedus 2012', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/22773292/',
    refFull: 'Hegedus EJ et al. Br J Sports Med 2012;46:964-78. Meta-analysis of shoulder physical exam.',
    notes: 'Sensitive but non-specific. Useful as a screening test, but a positive Hawkins alone is not diagnostic.',
  },
  'Neer impingement': {
    condition: 'Subacromial impingement',
    sens: 0.72, spec: 0.60, lr_pos: 1.8, lr_neg: 0.47,
    ref: 'Hegedus 2012', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/22773292/',
    refFull: 'Hegedus EJ et al. Br J Sports Med 2012;46:964-78.',
    notes: 'Comparable to Hawkins. Combine with Empty Can + painful arc for higher predictive value.',
  },
  'Empty Can (Jobe)': {
    condition: 'Supraspinatus tear / tendinopathy',
    sens: 0.69, spec: 0.62, lr_pos: 1.82, lr_neg: 0.50,
    ref: 'Hanchard 2013', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/23728635/',
    refFull: 'Hanchard NCA et al. Cochrane Database Syst Rev 2013. Special tests for rotator-cuff dysfunction.',
    notes: 'Reproduces pain or weakness in 90° abduction with IR. Combined with Hawkins + painful arc → LR+ 10.6 for full-thickness tear (Park 2005).',
  },
  'External rotation lag sign': {
    condition: 'Supraspinatus / infraspinatus full-thickness tear',
    sens: 0.46, spec: 0.94, lr_pos: 7.2, lr_neg: 0.57,
    ref: 'Hertel 1996', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/8945175/',
    refFull: 'Hertel R et al. J Shoulder Elbow Surg 1996;5:307-13.',
    notes: 'Very specific — positive lag strongly suggests full-thickness tear, especially of infraspinatus.',
  },
  'Lift-off (Gerber)': {
    condition: 'Subscapularis tear',
    sens: 0.62, spec: 0.98, lr_pos: 33.0, lr_neg: 0.39,
    ref: 'Gerber 1996', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/8895041/',
    refFull: 'Gerber C, Hersche O, Farron A. J Bone Joint Surg Br 1996;78:1015-23.',
    notes: 'Highly specific — a positive lift-off is essentially diagnostic of subscapularis tear.',
  },
  "Speed's test": {
    condition: 'Long-head biceps tendinopathy / SLAP',
    sens: 0.32, spec: 0.75, lr_pos: 1.3, lr_neg: 0.91,
    ref: 'Calis 2000', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/10691112/',
    refFull: 'Calis M et al. Ann Rheum Dis 2000;59:44-7.',
    notes: 'Limited diagnostic utility in isolation. Better as a part of a cluster.',
  },
  'Drop-arm test': {
    condition: 'Full-thickness rotator cuff tear',
    sens: 0.27, spec: 0.88, lr_pos: 2.25, lr_neg: 0.83,
    ref: 'Murrell 2001', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/11214129/',
    refFull: 'Murrell GAC, Walton JR. Lancet 2001;357:769-70.',
    notes: 'Positive drop is highly suggestive of a sizable cuff tear; negative does not rule out partial tears.',
  },
  'Apprehension test': {
    condition: 'Anterior glenohumeral instability',
    sens: 0.72, spec: 0.96, lr_pos: 17.6, lr_neg: 0.29,
    ref: 'Lo 2004', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/15090380/',
    refFull: 'Lo IK et al. Am J Sports Med 2004;32:301-7.',
    notes: 'Apprehension (not just pain) is a near-conclusive sign of anterior instability. Combine with Relocation for cluster.',
  },
  "O'Brien (Active Compression)": {
    condition: 'SLAP tear / AC joint pathology',
    sens: 0.63, spec: 0.50, lr_pos: 1.26, lr_neg: 0.74,
    ref: "O'Brien 1998", refUrl: 'https://pubmed.ncbi.nlm.nih.gov/9850774/',
    refFull: "O'Brien SJ et al. Am J Sports Med 1998;26:610-3.",
    notes: 'Original paper reported very high accuracy; subsequent studies far less so. Use as a cluster component.',
  },

  // ─────── Elbow / Wrist / Hand ───────
  "Cozen's test": {
    condition: 'Lateral epicondylitis (tennis elbow)',
    sens: 0.84, spec: 0.40, lr_pos: 1.4, lr_neg: 0.40,
    ref: 'Saroja 2014', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/24987323/',
    refFull: 'Saroja G et al. Int J Phys Med Rehabil 2014;S5:003.',
    notes: 'Sensitive screen for lateral epicondylitis. Negative test useful to rule out.',
  },
  "Phalen's sign": {
    condition: 'Carpal Tunnel Syndrome (CTS)',
    sens: 0.68, spec: 0.73, lr_pos: 2.52, lr_neg: 0.44,
    ref: 'Wainner 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/16235451/',
    refFull: 'Wainner RS et al. Arch Phys Med Rehabil 2005;86:609-18.',
    notes: 'Best diagnostic cluster: Hand Symptom Diagram + Phalen + Tinel + Symptoms within 1y. Cluster LR+ ≈ 16.',
  },
  'Tinel sign wrist': {
    condition: 'Carpal Tunnel Syndrome',
    sens: 0.50, spec: 0.77, lr_pos: 2.17, lr_neg: 0.65,
    ref: 'Wainner 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/16235451/',
    refFull: 'Wainner RS et al. Arch Phys Med Rehabil 2005;86:609-18.',
    notes: 'Moderately specific. Most useful when combined with Phalen + sensory loss in median distribution.',
  },
  'Finkelstein test': {
    condition: "De Quervain's tenosynovitis",
    sens: 0.81, spec: 0.50, lr_pos: 1.6, lr_neg: 0.38,
    ref: 'Elliott 1992', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/1469966/',
    refFull: 'Elliott BG. J Hand Surg Br 1992;17:481-2.',
    notes: 'Classic test for De Quervain — true Eichhoff manoeuvre (fist with thumb wrapped, then ulnar deviate) is more specific.',
  },

  // ─────── Hip ───────
  'FABER (Patrick)': {
    condition: 'SIJ pathology / hip joint / labral tear',
    sens: 0.41, spec: 0.83, lr_pos: 2.4, lr_neg: 0.71,
    ref: 'Magee 2014', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/23973908/',
    refFull: 'Reiman MP et al. Br J Sports Med 2013;47:893-902.',
    notes: 'Non-specific — positive can mean hip, SI, lumbar or pubic source. Use with Gaenslen + compression for SI cluster.',
  },
  'FADIR': {
    condition: 'Femoroacetabular Impingement (FAI) / labral tear',
    sens: 0.96, spec: 0.11, lr_pos: 1.08, lr_neg: 0.36,
    ref: 'Reiman 2013', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/23685702/',
    refFull: 'Reiman MP et al. Br J Sports Med 2013;47:893-902. Meta-analysis of hip impingement tests.',
    notes: 'Highly sensitive — a negative FADIR effectively rules out symptomatic FAI. Poor specificity → confirm with imaging.',
  },
  'Thomas test': {
    condition: 'Hip flexor tightness',
    sens: 0.89, spec: 0.92, lr_pos: 11.1, lr_neg: 0.12,
    ref: 'Harvey 1998', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/9617375/',
    refFull: 'Harvey D. Br J Sports Med 1998;32:68-70.',
    notes: 'Both sensitive AND specific for iliopsoas tightness — gold standard for hip-flexor length.',
  },
  'Trendelenburg': {
    condition: 'Gluteus medius weakness',
    sens: 0.55, spec: 0.70, lr_pos: 1.83, lr_neg: 0.64,
    ref: 'Vasudevan 1989', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/2924027/',
    refFull: 'Vasudevan PN et al. Indian J Orthop 1989;23:204-7.',
    notes: 'Limited diagnostic accuracy — most useful as a functional screen rather than to diagnose pathology.',
  },

  // ─────── Knee ───────
  'Lachman test': {
    condition: 'ACL tear',
    sens: 0.85, spec: 0.94, lr_pos: 14.2, lr_neg: 0.16,
    ref: 'Benjaminse 2006', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/16808982/',
    refFull: 'Benjaminse A et al. J Orthop Sports Phys Ther 2006;36:267-88. Meta-analysis of clinical tests for ACL.',
    notes: 'Highly accurate — best single physical test for ACL tear. Specificity > 0.94 in awake patients.',
  },
  'Anterior drawer knee': {
    condition: 'ACL tear',
    sens: 0.55, spec: 0.92, lr_pos: 6.9, lr_neg: 0.49,
    ref: 'Benjaminse 2006', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/16808982/',
    refFull: 'Benjaminse A et al. J Orthop Sports Phys Ther 2006;36:267-88.',
    notes: 'Specific but less sensitive than Lachman. Acute knees with hamstring guarding may give false negatives.',
  },
  'Pivot shift': {
    condition: 'ACL tear',
    sens: 0.24, spec: 0.98, lr_pos: 12.0, lr_neg: 0.78,
    ref: 'Benjaminse 2006', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/16808982/',
    refFull: 'Benjaminse A et al. J Orthop Sports Phys Ther 2006;36:267-88.',
    notes: 'Pathognomonic when positive — but very hard to elicit in an awake patient. Highly specific (under anaesthesia near-100%).',
  },
  "McMurray's test": {
    condition: 'Meniscal tear',
    sens: 0.55, spec: 0.77, lr_pos: 2.39, lr_neg: 0.58,
    ref: 'Hegedus 2007', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/17653109/',
    refFull: 'Hegedus EJ et al. JOSPT 2007;37:541-50. Meta-analysis of meniscal tests.',
    notes: 'Moderate accuracy. A "thud/clunk" plus joint-line pain is more specific than pain alone.',
  },
  'Thessaly test (20°)': {
    condition: 'Meniscal tear',
    sens: 0.75, spec: 0.87, lr_pos: 5.77, lr_neg: 0.29,
    ref: 'Karachalios 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/15805204/',
    refFull: 'Karachalios T et al. J Bone Joint Surg Am 2005;87:955-62.',
    notes: 'Best clinical test for meniscal tear — combines weight-bearing rotation. 20° gives better accuracy than 5°.',
  },
  'Valgus stress test': {
    condition: 'MCL tear',
    sens: 0.91, spec: 0.49, lr_pos: 1.78, lr_neg: 0.18,
    ref: 'Magee 2014', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/18234722/',
    refFull: 'Malanga GA et al. Arch Phys Med Rehabil 2003;84:592-603.',
    notes: 'Sensitive — useful screen for MCL injury. Grade laxity (0°=mild, 5-10mm=Gr 2, >10mm=Gr 3).',
  },
  'Varus stress test': {
    condition: 'LCL tear',
    sens: 0.25, spec: 0.97, lr_pos: 8.3, lr_neg: 0.77,
    ref: 'Magee 2014', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/18234722/',
    refFull: 'Malanga GA et al. Arch Phys Med Rehabil 2003;84:592-603.',
    notes: 'Specific but poorly sensitive — a positive test confirms, but negative does not exclude.',
  },

  // ─────── Ankle / Foot ───────
  'Anterior drawer ankle': {
    condition: 'ATFL injury / lateral ankle instability',
    sens: 0.71, spec: 0.33, lr_pos: 1.06, lr_neg: 0.88,
    ref: 'Vaseenon 2012', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/22832162/',
    refFull: 'Vaseenon T et al. Orthop Rev (Pavia) 2012;4:e16.',
    notes: 'Moderately sensitive. Combine with palpation tenderness over ATFL for better diagnostic yield.',
  },
  'Talar tilt': {
    condition: 'CFL injury / instability',
    sens: 0.50, spec: 0.88, lr_pos: 4.17, lr_neg: 0.57,
    ref: 'Vaseenon 2012', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/22832162/',
    refFull: 'Vaseenon T et al. Orthop Rev (Pavia) 2012;4:e16.',
    notes: 'Specific for CFL injury — most useful in chronic instability assessment.',
  },
  'Thompson test': {
    condition: 'Achilles tendon rupture',
    sens: 0.96, spec: 0.93, lr_pos: 13.7, lr_neg: 0.04,
    ref: 'Maffulli 1998', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/9659273/',
    refFull: 'Maffulli N. Am J Sports Med 1998;26:266-70.',
    notes: 'Excellent — high sensitivity AND specificity. Considered gold standard physical exam for Achilles rupture.',
  },

  // ─────── Cervical ───────
  "Spurling's test": {
    condition: 'Cervical radiculopathy',
    sens: 0.30, spec: 0.93, lr_pos: 4.29, lr_neg: 0.75,
    ref: 'Wainner 2003', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/12642762/',
    refFull: 'Wainner RS et al. Spine 2003;28:52-62. Reliability and accuracy of cervical exam tests.',
    notes: 'Highly specific — positive Spurling strongly suggests root irritation. Part of Wainner cluster (LR+ 30 when 4/4 positive).',
  },
  'Cervical distraction': {
    condition: 'Cervical radiculopathy',
    sens: 0.44, spec: 0.90, lr_pos: 4.4, lr_neg: 0.62,
    ref: 'Wainner 2003', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/12642762/',
    refFull: 'Wainner RS et al. Spine 2003;28:52-62.',
    notes: 'Specific for radiculopathy — symptom relief on distraction implicates root compression.',
  },
  'ULTT 1 (median)': {
    condition: 'Cervical radiculopathy (any level)',
    sens: 0.97, spec: 0.22, lr_pos: 1.24, lr_neg: 0.13,
    ref: 'Wainner 2003', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/12642762/',
    refFull: 'Wainner RS et al. Spine 2003;28:52-62.',
    notes: 'Highly sensitive — negative test rules out cervical radiculopathy.',
  },

  // ─────── Thoraco-lumbar / SIJ ───────
  'SLR (Straight-Leg Raise)': {
    condition: 'Lumbar disc herniation with radiculopathy',
    sens: 0.91, spec: 0.26, lr_pos: 1.23, lr_neg: 0.35,
    ref: 'Devillé 2000', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/10788861/',
    refFull: 'Devillé WLJM et al. Spine 2000;25:1140-7. Meta-analysis of SLR.',
    notes: 'Sensitive — negative SLR effectively rules out disc herniation as cause of leg pain.',
  },
  'Crossed SLR': {
    condition: 'Lumbar disc herniation',
    sens: 0.29, spec: 0.88, lr_pos: 2.42, lr_neg: 0.81,
    ref: 'Devillé 2000', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/10788861/',
    refFull: 'Devillé WLJM et al. Spine 2000;25:1140-7.',
    notes: 'Highly specific — positive crossed SLR strongly suggests large central or paramedian disc herniation.',
  },
  'Slump test': {
    condition: 'Lumbar disc / neural tension',
    sens: 0.84, spec: 0.83, lr_pos: 4.94, lr_neg: 0.19,
    ref: 'Stankovic 1999', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/10453713/',
    refFull: 'Stankovic R et al. Manual Therapy 1999;4:25-32.',
    notes: 'Excellent balance of sensitivity AND specificity. Stronger than SLR alone for sciatica.',
  },
  'Gaenslen': {
    condition: 'Sacroiliac joint dysfunction',
    sens: 0.68, spec: 0.71, lr_pos: 2.34, lr_neg: 0.45,
    ref: 'Laslett 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/15913076/',
    refFull: 'Laslett M et al. Manual Therapy 2005;10:207-18.',
    notes: 'Part of Laslett cluster — single test moderate accuracy. ≥3 of 5 SI provocation tests positive = best.',
  },
  'Distraction test SI': {
    condition: 'Sacroiliac joint dysfunction',
    sens: 0.60, spec: 0.81, lr_pos: 3.20, lr_neg: 0.49,
    ref: 'Laslett 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/15913076/',
    refFull: 'Laslett M et al. Manual Therapy 2005;10:207-18.',
    notes: 'Specific component of the Laslett SI cluster.',
  },
  'Thigh thrust': {
    condition: 'Sacroiliac joint dysfunction',
    sens: 0.88, spec: 0.69, lr_pos: 2.83, lr_neg: 0.18,
    ref: 'Laslett 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/15913076/',
    refFull: 'Laslett M et al. Manual Therapy 2005;10:207-18.',
    notes: 'Most sensitive single SI provocation test. Always pair with at least 2 others.',
  },
  'Compression test SI': {
    condition: 'Sacroiliac joint dysfunction',
    sens: 0.69, spec: 0.69, lr_pos: 2.23, lr_neg: 0.45,
    ref: 'Laslett 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/15913076/',
    refFull: 'Laslett M et al. Manual Therapy 2005;10:207-18.',
    notes: 'Moderate accuracy — part of the Laslett cluster.',
  },
  'Sacral thrust': {
    condition: 'Sacroiliac joint dysfunction',
    sens: 0.63, spec: 0.75, lr_pos: 2.52, lr_neg: 0.49,
    ref: 'Laslett 2005', refUrl: 'https://pubmed.ncbi.nlm.nih.gov/15913076/',
    refFull: 'Laslett M et al. Manual Therapy 2005;10:207-18.',
    notes: 'Component of the Laslett SI cluster. ≥3/5 positive provocation tests → LR+ 4.16.',
  },
};

/** Helper: classify accuracy strength for badges. */
export function strengthOf(metric, value) {
  if (value == null) return 'unknown';
  if (metric === 'sens' || metric === 'spec') {
    if (value >= 0.85) return 'strong';
    if (value >= 0.70) return 'moderate';
    return 'weak';
  }
  if (metric === 'lr_pos') {
    if (value >= 10) return 'strong';
    if (value >= 5)  return 'moderate';
    return 'weak';
  }
  if (metric === 'lr_neg') {
    if (value <= 0.1) return 'strong';
    if (value <= 0.2) return 'moderate';
    return 'weak';
  }
  return 'unknown';
}
